/**
 * src/engine/tax.ts — capital-gains math (§6.12, §6.14). Average-cost basis model.
 */
import type { TaxFields } from '@/types';

export { GENERIC_TAX_DEFAULTS } from '@/types';

/** combinedCapGainsRate = (longTerm + state)/100, as a fraction (§6.12). */
export function combinedCapGainsRate(
  tax: Pick<TaxFields, 'longTermCapGainsRatePct' | 'stateCapGainsRatePct'>,
): number {
  return (tax.longTermCapGainsRatePct + tax.stateCapGainsRatePct) / 100;
}

/** gainFraction = max(0, 1 − basis/portfolio); guards a zero/negative portfolio (§6.12). */
export function gainFraction(basis: number, portfolio: number): number {
  if (portfolio <= 0) return 0;
  return Math.max(0, 1 - basis / portfolio);
}

export interface SellGrossUp {
  grossSale: number;
  /** True when the gross-up denominator ≤ 0 (gain × rate ≥ 1) and the sale is capped (§6.14). */
  denomInvalid: boolean;
}

/**
 * Gross sale needed to net `net` after tax: S = net / (1 − gainFraction × combinedRate).
 * If the denominator ≤ 0 the gross-up is unbounded; cap at `net` and flag (§6.14).
 */
export function sellGrossUp(net: number, gain: number, combinedRate: number): SellGrossUp {
  const denom = 1 - gain * combinedRate;
  if (denom <= 0) return { grossSale: net, denomInvalid: true };
  return { grossSale: net / denom, denomInvalid: false };
}

/** Realized tax on a gross sale: S × gainFraction × combinedRate (§6.12). */
export function realizedTaxOnSale(grossSale: number, gain: number, combinedRate: number): number {
  return grossSale * gain * combinedRate;
}

/** Return-of-basis portion of a sale: S × (1 − gainFraction); reduces running basis (§6.12). */
export function basisConsumedOnSale(grossSale: number, gain: number): number {
  return grossSale * (1 - gain);
}
